import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Section from "../../components/Section";
import TestTable from "../../components/TestTable";
import { useMutation, useQuery } from "@apollo/client";
import { ProfileContext } from "../../reducers/profile";
import { useParams } from "react-router";
import {
  GET_FINALIST,
  GET_FINALISTS_RATINGS,
  SET_FINALIST_RATING,
} from "../../graphql_tags";
import { cache } from "../../client";

const initScores = {
  rating_1: { init: 10 },
  rating_2: { init: 6 },
  rating_3: { init: 4 },
};

const initialRatings = [
  "is_skipped",
  "rating_1",
  "rating_2",
  "rating_3",
  "total",
];

const initialScores = {
  rating_1: null,
  rating_2: null,
  rating_3: null,
  total: null,
  is_skipped: false,
};

export default () => {
  const {
    state: { user_id, is_locked },
  } = useContext(ProfileContext);
  let { finalists_id } = useParams();
  const juries_id = user_id;

  const loadFinalist = useQuery(GET_FINALIST, {
    variables: { juries_id, finalists_id },
  });
  const [setFinalistRating] = useMutation(SET_FINALIST_RATING);

  const [scores, setScores] = useState(initialScores);
  const [isLoaded, setLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (!loadFinalist.loading) {
      const { finalist, rating } = loadFinalist.data;
      setScores({ ...initialScores, ...finalist, ...rating });
      setLoaded(true);
    }
  }, [loadFinalist.loading, finalists_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const dotValue = _dot(value);
    const n = e.target.id;

    const { error, data } = sumRatings(
      parseScoresByError({
        ...scores,
        [name]: dotValue,
        [`error_${n}`]: _checkTargetError(dotValue, name),
      }),
    );

    setScores(data);
    if (!error) postRatings(data);
  };

  const updateQueryData = (changes) => {
    const OPT_GET_FINALIST = {
      query: GET_FINALIST,
      variables: { juries_id, finalists_id },
    };
    const OPT_GET_FINALISTS = {
      query: GET_FINALISTS_RATINGS,
      variables: { juries_id },
    };

    const numericFields = ["rating_1", "rating_2", "rating_3", "total"];
    const preparedChanges = Object.fromEntries(
      Object.entries(changes).map(([key, value]) => [
        key,
        numericFields.includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== ""
          ? Number(value)
          : value,
      ]),
    );

    setFinalistRating({
      variables: { juries_id, finalists_id, ...preparedChanges },
    }).catch((error) => console.error(error));

    cache.updateQuery(OPT_GET_FINALIST, (data) =>
      data
        ? {
            rating: {
              ...data.rating,
              ...preparedChanges,
            },
            finalist: { ...data.finalist },
          }
        : null,
    );

    cache.updateQuery(OPT_GET_FINALISTS, (data) => {
      const finalistTotal = {
        is_skipped: changes.is_skipped,
        total: changes.total,
      };

      if (!!data) {
        const isFinalistRating = data.ratings.find(
          (key) => key.finalists_id === finalists_id,
        );

        return {
          ratings: isFinalistRating
            ? data.ratings.map((key) =>
                key.finalists_id === finalists_id
                  ? {
                      ...key,
                      ...finalistTotal,
                    }
                  : key,
              )
            : [
                ...data.ratings,
                {
                  finalists_id,
                  ...finalistTotal,
                },
              ],
          finalists: data.finalists,
        };
      } else return null;
    });
  };

  const postRatings = (obj) => {
    const changes = Object.assign(
      ...Object.keys(obj)
        .filter((key) => initialRatings.includes(key))
        .map((key) => ({ [key]: obj[key] })),
    );

    updateQueryData(changes);
  };

  const sumRatings = ({ error, data }) => {
    const predicate = ["rating_1", "rating_2", "rating_3"];

    const _total = _round(_sum(_ratings(data, predicate)), 2).toString();
    const isTotalZero = _total === "0";

    data = {
      ...data,
      total: error || isTotalZero ? null : _total,
      is_skipped: false,
    };

    return { error, data };
  };

  const _dot = (val) => (val.includes(",") ? val.replace(/,/g, ".") : val);

  const _sum = (obj) => Object.values(obj).reduce((a, b) => a + b);
  const _round = (v, d) => Number(Math.round(v + "e" + d) + "e-" + d);

  const _ratings = (obj, predicate) => {
    return Object.assign(
      ...Object.keys(obj)
        .filter((key) => predicate.includes(key))
        .filter((key) => !!key)
        .map((key) => ({ [key]: +obj[key] })),
    );
  };

  const _checkTargetError = (value, name) => {
    if (value > initScores[name].init) {
      return true;
    } else return !/^[0-9]+(\.[0-9]+)?$/g.test(value);
  };

  const parseScoresByError = (obj) => {
    let data = {};
    let error = false;

    Object.keys(obj).forEach((name) => {
      const isNameError = name.includes("error_");

      if (isNameError) {
        const isThisError = !!obj[name];

        if (isThisError) {
          if (!error) error = true;
          data = { ...data, [name]: obj[name] };
        }
      } else data = { ...data, [name]: obj[name] };
    });

    return { data, error };
  };

  const onClickSkip = () => {
    const skipped = {
      rating_1: null,
      rating_2: null,
      rating_3: null,
      is_skipped: true,
      total: null,
    };

    postRatings({ ...scores, ...skipped });

    const changes = {
      ...scores,
      ...skipped,
      error_1: false,
      error_2: false,
      error_3: false,
    };

    setScores(changes);
  };

  return (
    isLoaded && (
      <TestSection>
        <TestHeading>
          <p>{scores.name}</p>
          <p>{scores.project}</p>
        </TestHeading>
        <TestTable
          handleChange={handleChange}
          scores={scores}
          disabled={is_locked}
        />
        <TestInfo>*Допускаются дробные значения (десятичные дроби).</TestInfo>
        {!is_locked && (
          <TestFooter>
            <TestBtn onClick={onClickSkip}>
              Пропустить оценку проекта финалиста
              <br /> по техническим причинам
            </TestBtn>
          </TestFooter>
        )}
      </TestSection>
    )
  );
};

const TestSection = styled(Section)`
  font-style: normal;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const TestHeading = styled.div`
  p {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-style: normal;
    color: #000000;

    :not(:last-child) {
      font-weight: normal;
    }

    :last-child {
      margin-top: 10px;
      font-weight: 500;
      font-family: RobotoMedium, sans-serif;
    }
  }
`;

const TestFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TestBtn = styled.button`
  padding: 0 20px;
  margin-top: 40px;
  height: 60px;

  box-sizing: border-box;
  border-radius: 10px;

  font-size: 24px;
  font-weight: normal;
  color: #ffffff;
  cursor: pointer;
  border-width: 3px;
  border-style: solid;

  :nth-child(2) {
    background: ${({ isScoresError }) =>
      isScoresError ? "#DA5E50" : "#339760"};
    border-color: ${({ isScoresError }) =>
      isScoresError ? "#DA5E50" : "#339760"};
  }

  :nth-child(1) {
    text-align: left;
    background: #777777;
    border-color: #777777;
    font-style: normal;
    font-size: 14px;
    line-height: 16px;
  }
`;

const TestInfo = styled.div`
  margin-top: 20px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;

  color: #777777;
`;
